// src/utils/speech.js

/** SpeechRecognition helper */
export function getSpeechRecognition() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return SR ? new SR() : null;
}

/* ---------------------- Speech Synthesis helpers ---------------------- */

let cachedVoices = [];
let voicesReadyPromise = null;

function loadVoicesOnce() {
  if (voicesReadyPromise) return voicesReadyPromise;

  voicesReadyPromise = new Promise((resolve) => {
    const synth = window.speechSynthesis;
    const tryLoad = () => {
      cachedVoices = synth.getVoices() || [];
      if (cachedVoices.length) {
        resolve(cachedVoices);
      } else {
        // In some browsers voices populate asynchronously
        // We wait for the event and try again.
        synth.addEventListener("voiceschanged", () => {
          cachedVoices = synth.getVoices() || [];
          resolve(cachedVoices);
        }, { once: true });
      }
    };

    try {
      tryLoad();
    } catch (e) {
      console.error("Loading voices failed:", e);
      resolve([]); // resolve anyway to avoid hanging
    }
  });

  return voicesReadyPromise;
}

function pickVoice(preferredName = "Google UK English Female") {
  // 1) Exact match for Google UK English Female (Chrome)
  let v = cachedVoices.find(v => v && v.name === preferredName);
  if (v) return v;

  // 2) Contains match (covers minor naming differences)
  v = cachedVoices.find(v => v && /google uk english female/i.test(v.name));
  if (v) return v;

  // 3) Any en-GB voice (Safari/Firefox often expose "Daniel", "Moira", etc.)
  v = cachedVoices.find(v => v && (v.lang || "").toLowerCase() === "en-gb");
  if (v) return v;

  // 4) Any Google UK English (male/female) if present
  v = cachedVoices.find(v => v && /google uk english/i.test(v.name));
  if (v) return v;

  // 5) Any English voice
  v = cachedVoices.find(v => v && (v.lang || "").toLowerCase().startsWith("en-"));
  if (v) return v;

  // 6) Absolute fallback: first available
  return cachedVoices[0] || null;
}

/**
 * Speak text using Web Speech Synthesis.
 * Defaults to "Google UK English Female" when available, else sensible fallbacks.
 */
export async function speak(text, voiceName = "Google UK English Female", rate = 1, pitch = 1) {
  if (!window.speechSynthesis) {
    console.warn("Speech synthesis not supported in this browser.");
    return;
  }
  try {
    await loadVoicesOnce(); // ensure voices are populated
    const utter = new SpeechSynthesisUtterance(String(text || ""));
    const chosen = pickVoice(voiceName);
    if (chosen) utter.voice = chosen;

    // Tweakables
    utter.rate = rate;   // 0.1–10
    utter.pitch = pitch; // 0–2
    utter.lang = chosen?.lang || "en-GB";

    // Cancel any queued speech so we don't overlap
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch (e) {
    console.error("Speech synthesis failed:", e);
  }
}
